import * as React from 'react';

// Components
import ConnectWithButton from './ConnectWithButton';

// Hooks
import { useConnectWithFacebook } from '../../../auth-provider/connect-with-facebook/hook';
import { useThirdPartyLoginFlowEvent } from '../flow-event';
import { useAutoRestoreState } from '@tob/shared/utils/react-helper';

// Constants & Enums
import { AuthenticateChannel } from '../../../core/types';
import {
  THIRDPARTY_AUTH_PROVIDER_REDIRECT_URI,
  ThirdpartyBindingEntryPoint,
} from '@tob/shared/thirdparty-auth/service/constants';
import { isWebBridgeSupported } from '@shopee-user/platform';

// Types
import type { ConnectWithButtonProps } from './ConnectWithButton';
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';

import IconConnectWithFacebook from '@shopee-user/icons/IconConnectWithFacebook';
import styles from './style.scss';

type Props = Omit<ConnectWithButtonProps, 'channel'> & {
  entryPoint: ThirdpartyBindingEntryPoint;
  callbackUrl?: string;
  webViewAuthWith3PServiceByToken?: WithUserWebViewProps['webViewAuthWith3PServiceByToken'];
  onAuthenticated?: ({
    authCode,
    appClientId,
  }: {
    authCode: string;
    appClientId?: string;
  }) => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onError?: ({
    error,
    errorMsg,
  }: {
    error: number;
    errorMsg?: string;
  }) => void | Promise<void>;
  isAutoTriggered?: boolean;
};

export default function ConnectWithFacebook({
  className,
  label,
  layout,
  callbackUrl,
  webViewAuthWith3PServiceByToken,
  onAuthenticated,
  onCancel,
  onError,
  entryPoint,
  isAutoTriggered = false,
}: Props) {
  const [isDisabled, setIsDisabled] = useAutoRestoreState(false, {
    timeout: 3000,
  });
  const { authenticate } = useConnectWithFacebook({
    redirectUri: THIRDPARTY_AUTH_PROVIDER_REDIRECT_URI[entryPoint],
    entryPoint,
    webViewAuthWith3PServiceByToken,
  });
  const isInited = React.useRef(false);

  const { trackEvent: trackLoginEvent } = useThirdPartyLoginFlowEvent();

  const startAuthentication = React.useCallback(async () => {
    trackLoginEvent('onThirdPartyLoginClick', {
      channel: AuthenticateChannel.Facebook,
    });

    setIsDisabled(true);
    const {
      isCancel,
      error,
      errorMsg,
      authCode,
      appClientId,
    } = await authenticate();

    if (isCancel) {
      await onCancel?.();
      setIsDisabled(false);
      return;
    }

    if (error) {
      await onError?.({
        error,
        errorMsg,
      });
      setIsDisabled(false);
      return;
    }

    setIsDisabled(false);
    // On web, the page redirects to the provider and control never returns here —
    // the browser navigates away. Only the App (via web bridge) completes the
    // OAuth flow in-page and delivers an authCode back to this callback.
    if (isWebBridgeSupported()) {
      if (authCode) {
        await onAuthenticated?.({ authCode, appClientId });
      } else {
        await onError?.({
          error: 1,
          errorMsg: 'Missing authCode',
        });
      }
    }
  }, [
    authenticate,
    callbackUrl,
    onAuthenticated,
    onCancel,
    onError,
    setIsDisabled,
    trackLoginEvent,
  ]);

  React.useEffect(() => {
    if (isInited.current) return;
    isInited.current = true;
    trackLoginEvent('onThirdPartyLoginComponentView', {
      channel: AuthenticateChannel.Facebook,
    });
    if (isAutoTriggered) {
      startAuthentication();
    }
  }, []);

  return (
    <ConnectWithButton
      channel={AuthenticateChannel.Facebook}
      className={className}
      disabled={isDisabled}
      label={label}
      icon={<IconConnectWithFacebook className={styles.icon} />}
      layout={layout}
      onClick={startAuthentication}
    />
  );
}
